/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment-timezone';
import {
  Box,
  Button,
  CircularProgress,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useConfirm } from 'material-ui-confirm';
import { createEmptyTask, MEDIA_TYPES } from '../Task.model';
import { TASK_VALIDATION_MODES, validateTask } from '../Task.validation';
import adminFormStyles from '../../shared/styles/adminForm.styles';
import TaskContentRequestsForm from './TaskContentRequestsForm';
import TaskTargetingPlaceholder from './TaskTargetingPlaceholder';

const styles = {
  ...adminFormStyles,
  backIcon: {
    fontSize: '14px !important',
  },
};

// Content item errors are keyed by ui_id, not by position.
const NO_ERRORS = { content_requests: {} };

// Compare day-level dates and item fields only (ignore ui_id).
function hasTaskChanges(task, baseline) {
  const dueDatesMatch =
    (!task.due_date && !baseline.due_date) ||
    (task.due_date &&
      baseline.due_date &&
      moment(task.due_date).isSame(moment(baseline.due_date), 'day'));

  const normalizeContentRequests = (contentRequests) =>
    (contentRequests || []).map(({ id, title, description, media_type }) => ({
      id: id ?? null,
      title,
      description,
      media_type,
    }));

  return (
    task.title !== baseline.title ||
    task.description !== baseline.description ||
    !dueDatesMatch ||
    !_.isEqual(
      normalizeContentRequests(task.content_requests),
      normalizeContentRequests(baseline.content_requests)
    )
  );
}

export default function TaskForm({
  initialTask,
  isSaving,
  onBack,
  onSaveDraft,
  onLaunch,
}) {
  const confirm = useConfirm();
  const [task, setTask] = useState(() => initialTask || createEmptyTask());
  const [validationErrors, setValidationErrors] = useState(NO_ERRORS);
  const isEditingLaunchedTask = Boolean(task.task_id && task.launch_date);

  useEffect(() => {
    setTask(initialTask || createEmptyTask());
    setValidationErrors(NO_ERRORS);
  }, [initialTask]);

  function updateTask(field, value) {
    setTask((currentTask) => ({
      ...currentTask,
      [field]: value,
    }));
  }

  function updateContentRequests(contentRequests) {
    updateTask('content_requests', contentRequests);
    dropErrorsForRemovedContentRequests(contentRequests);
  }

  function dropErrorsForRemovedContentRequests(contentRequests) {
    setValidationErrors((currentErrors) => {
      const remainingErrors = _.pick(
        currentErrors.content_requests,
        _.map(contentRequests, 'ui_id')
      );

      if (_.size(remainingErrors) === _.size(currentErrors.content_requests)) {
        return currentErrors;
      }

      return {
        ...currentErrors,
        content_requests: remainingErrors,
        // Removing the last incomplete item resolves the summary too.
        ...(_.isEmpty(remainingErrors)
          ? { content_requests_summary: undefined }
          : {}),
      };
    });
  }

  function clearValidationError(field) {
    setValidationErrors((currentErrors) => {
      if (!currentErrors[field]) return currentErrors;

      return {
        ...currentErrors,
        [field]: undefined,
      };
    });
  }

  function submit(mode, callback) {
    const result = validateTask(task, mode);
    setValidationErrors(result.errors);

    if (result.isValid) {
      callback(task);
    }
  }

  function saveDraft() {
    submit(TASK_VALIDATION_MODES.DRAFT, onSaveDraft);
  }

  function launchTask() {
    submit(TASK_VALIDATION_MODES.LAUNCH, onLaunch);
  }

  async function handleBack() {
    const baseline = initialTask || createEmptyTask();
    if (!hasTaskChanges(task, baseline)) {
      onBack();
      return;
    }

    try {
      await confirm({
        title: 'Save your changes?',
        description:
          'You have unsaved changes. Would you like to save before leaving?',
        confirmationText: 'Save',
        cancellationText: "Don't save",
      });
    } catch {
      // "Don't save" (confirm rejects on cancel).
      onBack();
      return;
    }

    const result = validateTask(task, TASK_VALIDATION_MODES.DRAFT);
    setValidationErrors(result.errors);
    if (!result.isValid) return;

    await onSaveDraft(task);
    onBack();
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing="20px">
        <Box>
          <Button
            color="inherit"
            disableRipple
            startIcon={<ArrowBack sx={styles.backIcon} />}
            size="small"
            sx={styles.backButton}
            onClick={handleBack}
          >
            Content requests
          </Button>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography component="h1" sx={styles.pageTitle}>
              {task.task_id ? 'Edit content request' : 'Create content request'}
            </Typography>
            {!isEditingLaunchedTask && (
              <Button
                type="button"
                variant="outlined"
                onClick={saveDraft}
                disabled={isSaving}
                startIcon={
                  isSaving ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null
                }
                sx={styles.secondaryButton}
              >
                {isSaving ? 'Saving...' : 'Save as draft'}
              </Button>
            )}
          </Stack>
        </Box>

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <Box>
              <Typography component="h2" sx={styles.sectionTitle}>
                Request details
              </Typography>
              <Typography sx={styles.sectionDescription}>
                This is what associates see at the top of the task.
              </Typography>
            </Box>

            <Stack spacing={0.75}>
              <FormLabel htmlFor="task-title" sx={styles.fieldLabel}>
                Title
              </FormLabel>
              <TextField
                fullWidth
                id="task-title"
                placeholder="e.g. Valentine’s Day Campaign"
                value={task.title}
                onChange={(event) => {
                  const { value } = event.target;
                  updateTask('title', value);
                  if (value.trim()) clearValidationError('title');
                }}
                error={Boolean(validationErrors.title)}
                helperText={validationErrors.title}
                sx={styles.field}
              />
            </Stack>

            <Stack spacing={0.75}>
              <FormLabel htmlFor="task-description" sx={styles.fieldLabel}>
                Description
              </FormLabel>
              <TextField
                fullWidth
                id="task-description"
                multiline
                minRows={2}
                placeholder="Describe the media request for store associates"
                value={task.description}
                onChange={(event) =>
                  updateTask('description', event.target.value)
                }
                error={Boolean(validationErrors.description)}
                helperText={validationErrors.description}
                sx={styles.field}
              />
              <Typography sx={styles.helperText}>
                Plain language, sentence case.
              </Typography>
            </Stack>

            <Stack spacing={0.75} maxWidth="300px">
              <FormLabel htmlFor="task-deadline" sx={styles.fieldLabel}>
                Deadline
              </FormLabel>
              <DatePicker
                format="MM/DD/YYYY"
                value={task.due_date}
                onChange={(value) => updateTask('due_date', value)}
                slotProps={{
                  textField: {
                    id: 'task-deadline',
                    error: Boolean(validationErrors.due_date),
                    helperText: validationErrors.due_date,
                    placeholder: 'MM/DD/YYYY',
                  },
                }}
                sx={styles.field}
              />
              <Typography sx={styles.helperText}>
                One deadline applies to the whole request.
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <TaskContentRequestsForm
          contentRequests={task.content_requests}
          errors={validationErrors.content_requests}
          onChange={updateContentRequests}
          summaryError={validationErrors.content_requests_summary}
        />

        <TaskTargetingPlaceholder />

        <Paper component="footer" variant="outlined" sx={styles.section}>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            padding="16px 20px"
          >
            {!isEditingLaunchedTask && (
              <Button
                type="button"
                variant="outlined"
                onClick={saveDraft}
                disabled={isSaving}
                startIcon={
                  isSaving ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null
                }
                sx={styles.secondaryButton}
              >
                {isSaving ? 'Saving...' : 'Save as draft'}
              </Button>
            )}
            <Button
              type="button"
              variant="contained"
              onClick={launchTask}
              disabled={isSaving}
              sx={styles.primaryButton}
            >
              {isEditingLaunchedTask ? 'Update request' : 'Post request'}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </LocalizationProvider>
  );
}

TaskForm.propTypes = {
  initialTask: PropTypes.shape({
    task_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    task_type: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    targeting: PropTypes.shape({
      states: PropTypes.arrayOf(PropTypes.string),
      markets: PropTypes.arrayOf(PropTypes.string),
      regions: PropTypes.arrayOf(PropTypes.string),
      store_numbers: PropTypes.arrayOf(PropTypes.string),
      social_champs: PropTypes.arrayOf(PropTypes.string),
      employeeTypes: PropTypes.arrayOf(PropTypes.string),
    }),
    launch_date: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    due_date: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    launched_by: PropTypes.string,
    content_requests: PropTypes.arrayOf(
      PropTypes.shape({
        ui_id: PropTypes.string.isRequired,
        id: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
        media_type: PropTypes.oneOf(['', ...Object.values(MEDIA_TYPES)]),
      })
    ),
  }),
  isSaving: PropTypes.bool,
  onBack: PropTypes.func,
  onSaveDraft: PropTypes.func,
  onLaunch: PropTypes.func,
};

TaskForm.defaultProps = {
  initialTask: null,
  isSaving: false,
  onBack: () => {},
  onSaveDraft: () => {},
  onLaunch: () => {},
};
